/* eslint-disable @typescript-eslint/naming-convention */
import {EDocType} from 'constants/index';
import type {IFdApplicationDocType} from 'constants/interfaces';

export type DocumentCategoryIcon =
  | 'building'
  | 'file'
  | 'handshake'
  | 'id-card'
  | 'chart-line'
  | 'folder-open';

export interface DocumentCategory {
  id: string;
  name: string;
  description?: string;
  icon: DocumentCategoryIcon;
  order: number;
}

export interface DocumentRequirement {
  id: string;
  name: string;
  categoryId: string;
  isRequired: boolean;
  allowMultiple?: boolean;
  helperText?: string;
  acceptedMimeTypes?: string[];
  maxFileSizeMb?: number;
  uploadDocumentType: EDocType;
  matchingDocumentTypes?: string[];
}

export interface DocumentUploadFile {
  id?: number;
  uid: string;
  requirementId: string;
  name: string;
  url?: string;
  size?: number;
  uploadedAt: string;
  status: 'uploaded' | 'uploading' | 'failed';
  progress: number;
  error?: string | null;
  extension?: string;
  documentType?: string;
}

export interface DocumentRequirementGroup {
  category: DocumentCategory;
  requirements: DocumentRequirement[];
}

export const DEFAULT_ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export const DEFAULT_ACCEPTED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'pdf',
  'doc',
  'docx',
] as const;

export const DEFAULT_MAX_FILE_SIZE_MB = 10;

const REQUIRED = true;
const OPTIONAL = false;

export const DOCUMENT_CATEGORIES: DocumentCategory[] = [
  {
    id: 'personnel-identification',
    name: 'Personnel identification',
    icon: 'id-card',
    order: 1,
    description:
      'Identity documents for applicant, directors and key stakeholders.',
  },
  {
    id: 'business-information',
    name: 'Business information',
    icon: 'building',
    order: 2,
    description:
      'Company registration, compliance certificates and trading documents.',
  },
  {
    id: 'financial-records',
    name: 'Financial records',
    icon: 'chart-line',
    order: 3,
    description: 'Historic financial statements and banking proof.',
  },
  {
    id: 'property-agreements',
    name: 'Property & agreements',
    icon: 'handshake',
    order: 4,
    description: 'Land ownership, leases and supplier agreements.',
  },
  {
    id: 'address-verification',
    name: 'Address verification',
    icon: 'file',
    order: 5,
    description: 'Proof of operating and registered business addresses.',
  },
  {
    id: 'legal-documents',
    name: 'Legal documents',
    icon: 'file',
    order: 6,
    description: 'Consent forms and supporting legal affidavits.',
  },
  {
    id: 'other-documents',
    name: 'Other documents',
    icon: 'folder-open',
    order: 7,
    description: 'Additional supporting documents not covered above.',
  },
];

export const DOCUMENT_REQUIREMENTS: DocumentRequirement[] = [
  {
    id: EDocType.CIPC,
    name: 'CIPC CoR 14.3',
    categoryId: 'business-information',
    isRequired: REQUIRED,
    helperText: 'Official company registration documents from CIPC.',
    uploadDocumentType: EDocType.CIPC,
  },
  {
    id: EDocType.BBBEE_CERTIFICATE,
    name: 'B-BBEE Certificate (optional)',
    categoryId: 'business-information',
    isRequired: OPTIONAL,
    helperText: 'Latest B-BBEE certificate or sworn affidavit if applicable.',
    uploadDocumentType: EDocType.BBBEE_CERTIFICATE,
  },
  {
    id: EDocType.SARS_TAX_PIN,
    name: 'SARS Tax Pin',
    categoryId: 'business-information',
    isRequired: REQUIRED,
    helperText: 'Provide the latest SARS tax pin for verification.',
    uploadDocumentType: EDocType.SARS_TAX_PIN,
  },
  {
    id: EDocType.VAT_CERTIFICATE,
    name: 'VAT Certificate',
    categoryId: 'business-information',
    isRequired: OPTIONAL,
    helperText: 'Upload the VAT registration certificate if registered.',
    uploadDocumentType: EDocType.VAT_CERTIFICATE,
  },
  {
    id: EDocType.OTHER,
    name: 'Other documents',
    categoryId: 'other-documents',
    isRequired: OPTIONAL,
    allowMultiple: true,
    helperText:
      'Any additional business documents that support your application.',
    uploadDocumentType: EDocType.OTHER,
  },
  {
    id: EDocType.TITLE_DEED,
    name: 'Title deed',
    categoryId: 'property-agreements',
    isRequired: REQUIRED,
    helperText: 'Proof of ownership for farmland or property used.',
    uploadDocumentType: EDocType.TITLE_DEED,
  },
  {
    id: EDocType.WATER_RIGHT,
    name: 'Water rights',
    categoryId: 'property-agreements',
    isRequired: REQUIRED,
    helperText:
      'Documentation confirming water rights for irrigation and production.',
    uploadDocumentType: EDocType.WATER_RIGHT,
  },
  {
    id: EDocType.LEASE_AGREEMENT,
    name: 'Lease agreement',
    categoryId: 'property-agreements',
    isRequired: REQUIRED,
    helperText: 'Signed lease agreements for any rented land or facilities.',
    uploadDocumentType: EDocType.LEASE_AGREEMENT,
  },
  {
    id: EDocType.SUPPLIER_MOU,
    name: 'Supplier MOU',
    categoryId: 'property-agreements',
    isRequired: OPTIONAL,
    helperText:
      'Memorandums of understanding with key suppliers, if available.',
    uploadDocumentType: EDocType.SUPPLIER_MOU,
  },
  {
    id: EDocType.APPLICANTS_ID,
    name: 'Applicant ID document',
    categoryId: 'personnel-identification',
    isRequired: REQUIRED,
    helperText: 'Government-issued identification for the primary applicant.',
    uploadDocumentType: EDocType.APPLICANTS_ID,
  },
  {
    id: EDocType.DIRECTORS_ID,
    name: 'Directors ID documents',
    categoryId: 'personnel-identification',
    isRequired: REQUIRED,
    allowMultiple: true,
    helperText: 'Upload IDs for all directors or key shareholders.',
    uploadDocumentType: EDocType.DIRECTORS_ID,
  },
  {
    id: EDocType.MARRIAGE_CERTIFICATE,
    name: 'Marriage certificate (if applicable)',
    categoryId: 'personnel-identification',
    isRequired: OPTIONAL,
    helperText:
      'Include the marriage certificate if ownership or surnames differ.',
    uploadDocumentType: EDocType.MARRIAGE_CERTIFICATE,
  },
  {
    id: EDocType.AFS3,
    name: 'Past 3 years AFS',
    categoryId: 'financial-records',
    isRequired: REQUIRED,
    helperText:
      'Historical financial statements up to three years to demonstrate trends.',
    uploadDocumentType: EDocType.AFS3,
    matchingDocumentTypes: [EDocType.AFS1, EDocType.AFS2, EDocType.AFS3],
  },
  {
    id: EDocType.BANK_STATEMENT,
    name: 'Bank statements (6-12 months)',
    categoryId: 'financial-records',
    isRequired: REQUIRED,
    allowMultiple: true,
    helperText:
      'Upload consolidated PDF statements covering at least the last 6 months.',
    uploadDocumentType: EDocType.BANK_STATEMENT,
  },
  {
    id: EDocType.PROOF_OF_BANKING,
    name: 'Proof of banking details',
    categoryId: 'financial-records',
    isRequired: REQUIRED,
    helperText: 'Bank confirmation letter or stamped proof of banking.',
    uploadDocumentType: EDocType.PROOF_OF_BANKING,
  },
  {
    id: EDocType.BANK_COMFIRMATIVE_LETTER,
    name: 'Bank confirmation letter',
    categoryId: 'financial-records',
    isRequired: REQUIRED,
    helperText:
      'Confirmation letter from your bank validating the account details.',
    uploadDocumentType: EDocType.BANK_COMFIRMATIVE_LETTER,
  },
  {
    id: EDocType.PROOF_OF_ADDRESS,
    name: 'Proof of address',
    categoryId: 'address-verification',
    isRequired: REQUIRED,
    helperText: 'Utility bill or municipal statement not older than 3 months.',
    uploadDocumentType: EDocType.PROOF_OF_ADDRESS,
  },
  {
    id: EDocType.CONSENT_FORM,
    name: 'Consent form',
    categoryId: 'legal-documents',
    isRequired: REQUIRED,
    helperText: 'Signed consent form authorising Khula to assess the data.',
    uploadDocumentType: EDocType.CONSENT_FORM,
  },
  {
    id: EDocType.AFFIDAVIT,
    name: 'Affidavit',
    categoryId: 'legal-documents',
    isRequired: OPTIONAL,
    helperText:
      'Any sworn affidavit supporting exceptional circumstances or ownership claims.',
    uploadDocumentType: EDocType.AFFIDAVIT,
  },
];

DOCUMENT_REQUIREMENTS.forEach((requirement) => {
  requirement.acceptedMimeTypes = requirement.acceptedMimeTypes ?? [
    ...DEFAULT_ACCEPTED_MIME_TYPES,
  ];
  requirement.maxFileSizeMb =
    requirement.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
  requirement.matchingDocumentTypes = requirement.matchingDocumentTypes ?? [
    requirement.uploadDocumentType,
  ];
});

export const DOCUMENT_REQUIREMENT_MAP = DOCUMENT_REQUIREMENTS.reduce<
  Record<string, DocumentRequirement>
>((acc, requirement) => {
  acc[requirement.id] = requirement;
  return acc;
}, {});

const DOCUMENT_TYPE_TO_REQUIREMENT_ID = DOCUMENT_REQUIREMENTS.reduce<
  Record<string, string>
>((acc, requirement) => {
  (
    requirement.matchingDocumentTypes || [requirement.uploadDocumentType]
  ).forEach((documentType) => {
    acc[documentType] = requirement.id;
  });
  return acc;
}, {});

const FILE_EXTENSION_REGEX = /\.([a-z0-9]{2,8})$/i;

function extractExtension(name?: string | null) {
  if (!name) return undefined;
  const match = name.match(FILE_EXTENSION_REGEX);
  return match ? match[1].toLowerCase() : undefined;
}

export function getRequirementById(id: string) {
  return DOCUMENT_REQUIREMENT_MAP[id];
}

export function resolveRequirementIdForDocumentType(documentType: unknown) {
  const key = String(documentType || '').trim();
  return DOCUMENT_TYPE_TO_REQUIREMENT_ID[key] || key;
}

export function getGroupedDocumentRequirements(): DocumentRequirementGroup[] {
  const byCategory: Record<string, DocumentRequirement[]> = {};
  DOCUMENT_REQUIREMENTS.forEach((requirement) => {
    if (!byCategory[requirement.categoryId]) {
      byCategory[requirement.categoryId] = [];
    }
    byCategory[requirement.categoryId].push(requirement);
  });

  return [...DOCUMENT_CATEGORIES]
    .sort((left, right) => left.order - right.order)
    .map((category) => ({
      category,
      requirements: (byCategory[category.id] ?? []).sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    }));
}

export function adaptApplicationDocumentRecord(
  record: IFdApplicationDocType,
): DocumentUploadFile {
  const nameFromPath =
    record.filePath?.split('/').pop() ??
    `${record.documentType}_${record.id || Date.now()}`;
  const requirementId = resolveRequirementIdForDocumentType(
    record.documentType,
  );

  return {
    id: Number(record.id),
    uid: `record-${record.id}`,
    requirementId,
    name: nameFromPath,
    url: record.filePath,
    uploadedAt:
      record.updatedAt || record.createdAt || new Date().toISOString(),
    status: 'uploaded',
    progress: 100,
    extension: extractExtension(nameFromPath),
    documentType: String(record.documentType),
  };
}
